import React, { useState } from "react";
import { canRemoveComplianceItem } from "../constants/complianceChecklist";

const ComplianceChecklist = ({
  items,
  transactionType,
  onToggle,
  onAddCustom,
  onRemoveItem,
  comments,
  onCommentsChange,
}) => {
  const [draft, setDraft] = useState("");

  const mandatoryItems = items.filter((i) => i.group === "mandatory");
  const optionalItems = items.filter((i) => i.group === "optional");
  const customItems = items.filter((i) => i.group === "custom");

  const handleAdd = () => {
    const label = draft.trim();
    if (!label) return;
    onAddCustom?.(label);
    setDraft("");
  };

  const renderItem = (item) => (
    <li key={item.id} className="flex items-start gap-2">
      <input
        type="checkbox"
        id={`compliance-${item.id}`}
        checked={!!item.checked}
        onChange={() => onToggle(item.id)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-700"
      />
      <label
        htmlFor={`compliance-${item.id}`}
        className="text-sm text-gray-800 flex-1 leading-snug cursor-pointer"
      >
        {item.label}
      </label>
      {canRemoveComplianceItem(item) && onRemoveItem && (
        <button
          type="button"
          onClick={() => onRemoveItem(item.id)}
          className="text-xs text-red-600 hover:underline shrink-0"
          aria-label={`Remove ${item.label}`}
        >
          Remove
        </button>
      )}
    </li>
  );

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Compliance checklist
      </p>
      {!transactionType ? (
        <p className="text-sm text-gray-500 mb-3">
          Select a transaction type above to show the compliance checklist.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-3">
            Tick each item below. When every box is checked, compliance is set to
            Yes on the Progress Report.
          </p>

          {mandatoryItems.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Required forms
              </p>
              <ul className="space-y-2">{mandatoryItems.map(renderItem)}</ul>
            </div>
          )}

          {optionalItems.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Optional forms
              </p>
              <ul className="space-y-2">{optionalItems.map(renderItem)}</ul>
            </div>
          )}

          {customItems.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Additional forms
              </p>
              <ul className="space-y-2">{customItems.map(renderItem)}</ul>
            </div>
          )}

          {onAddCustom && (
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                placeholder="Add custom form…"
                className="flex-1 rounded-md border border-gray-300 text-sm px-2 py-1.5 focus:border-blue-700 focus:ring-blue-700"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={!draft.trim()}
                className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Add item
              </button>
            </div>
          )}
        </>
      )}

      <div className="pt-3 border-t border-gray-100">
        <label
          htmlFor="compliance-comments"
          className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
        >
          Comments
        </label>
        <textarea
          id="compliance-comments"
          value={comments || ""}
          onChange={(e) => onCommentsChange?.(e.target.value)}
          rows={3}
          placeholder="Enter comments…"
          className="w-full rounded-md border border-gray-300 text-sm px-3 py-2 focus:border-blue-700 focus:ring-blue-700"
        />
      </div>
    </div>
  );
};

export default ComplianceChecklist;
